import { ErreurApi } from '../../api/client.js';
import type { BilanRejeu, ConsentementAEnregistrer, Journal, TransportPush } from './dependances.js';
import {
  ATTENTE_REJEU,
  CE_QU_ELLE_DIT,
  CHAMPS_TEXTE_APPEL,
  COMPTE_RENDU_APPEL,
  CONSENTEMENT_REFUS_NUMERO,
  QUESTIONS_ENTRETIEN_INITIAL,
  REFUS_NUMERO,
  SITUATIONS_MATRIMONIALES,
  LIBELLES_A_REPRENDRE,
  LIBELLE_A_REPRENDRE_PAR_DEFAUT,
  MODELE_COMPTE_RENDU,
  PREFIXE_ENTRETIEN,
  RUBRIQUES_APPEL,
  RUBRIQUE_ENTRETIEN_INITIAL,
  RUBRIQUE_ISSUE,
  RUBRIQUE_PAR_DEFAUT,
  TYPES_SYNC,
  type RubriqueAppel,
} from './donnees.js';

/**
 * Appel en cours (M02, CA-BRI-02a à 02d). Toute saisie est d'abord gardée sur l'appareil (journal de @lakou/sync), puis
 * envoyée si le réseau est là : en mode avion rien ne part et rien ne se perd, au retour du réseau tout part une fois.
 */
export interface ServicesAppel {
  journal: Journal;
  /**
   * Réserve de l'appareil pour les mutations dont le type n'est pas encore déclaré côté sync : gardées, jamais envoyées.
   * Absente : tout part par la file d'envoi (services sans politique, doublures de test).
   */
  differe?: Journal;
  /** Le serveur de synchronisation connaît-il ce type (nom de synchronisation) ? Absent : oui, pour tous. */
  estSynchronisee?: (entityType: string) => boolean;
  /** Rejoue la file d'envoi (transport `sync.push` en production). */
  rejouer: () => Promise<BilanRejeu>;
  /**
   * Enregistre un consentement par la route du contrat `confidentialite.consentements.enregistrer` (audit porté par la
   * route). Absent : le refus reste en réserve sur l'appareil. Une erreur `RESEAU` laisse le refus en attente ; toute autre
   * erreur le marque « à reprendre ».
   */
  enregistrerConsentement?: (personId: string, corps: ConsentementAEnregistrer) => Promise<unknown>;
  enLigne: () => boolean;
  agentId: string;
  horloge: () => string;
  genererId: () => string;
  /** Pause entre deux essais quand la file est déjà en cours de rejeu ailleurs. Absente : minuterie du navigateur. */
  attendre?: (ms: number) => Promise<void>;
  /**
   * Envoi par les routes directes du contrat (défaut assistante-01) : présent, M02 n'utilise jamais la file de synchronisation.
   * Les saisies restent sur l'appareil pendant l'appel ; à « Terminer », le compte rendu d'appel puis la clôture de la tâche partent.
   */
  envoyer?: EnvoiDirectAppel;
}

/** Routes directes de M02 : `comptesRendus.creer` (kind « appel ») et `taches.modifier` (statut « fait »). */
export interface EnvoiDirectAppel {
  compteRendu(dossierId: string, corps: { kind: 'appel'; title: string; personId: string; sourceText: string }): Promise<unknown>;
  tache(tacheId: string, version: number, corps: { status: 'fait' }): Promise<unknown>;
  /** Fiche de la personne (`genealogie.personne.modifier`, If-Match) : profession et situation recueillies (arbitrage du 26/10). */
  personne?(personId: string, version: number, corps: { profession?: string; maritalStatus?: string }): Promise<unknown>;
  /** Une coordonnée (`genealogie.coordonnees.ajouter`, chiffrée en base) : l'adresse recueillie. */
  coordonnee?(personId: string, corps: { type: 'address'; value: string }): Promise<unknown>;
}

/** Étape de l'envoi direct d'un appel clos, marquée sur l'appareil une fois faite : une reprise ne la rejoue pas. */
const TYPE_ETAPE = 'appel_etape';

export interface CibleAppel {
  personId: string;
  dossierId: string;
  versionPersonne: number;
  tacheId?: string;
  versionTache?: number;
}

export interface ResultatSaisie {
  etat: 'envoyee' | 'gardee' | 'a_reprendre';
  enAttente: number;
  /** Présent seulement quand le serveur a refusé des saisies. */
  aReprendre?: number;
}

export interface BilanFin {
  /** Vrai seulement quand le serveur a enregistré le refus (route du contrat) ; jamais « enregistré » sur simple saisie. */
  consentementEnregistre: boolean;
  /** Le compte rendu E2 est toujours un brouillon à relire : jamais envoyé sans validation (RG-19). */
  compteRendu: 'a_relire';
  /** Éléments refusés par le serveur (rejet ou conflit) : jamais perdus en silence, présentés « à reprendre ». */
  aReprendre: number;
  elementsARepriser: string[];
  /** Éléments gardés sur l'appareil parce que le serveur de synchronisation ne connaît pas encore leur type. */
  differees: number;
}

/** Refus de partage du numéro, gardé en réserve tant que la route du contrat ne l'a pas enregistré. */
export interface EtatConsentements {
  enAttente: number;
  /** Le serveur a refusé l'enregistrement (autre qu'une coupure réseau) : à reprendre par Brigitte. */
  aReprendre: boolean;
}

export interface EtatAppel {
  /** Mutations saisies, pas encore acquittées par le serveur (réseau absent ou coupé). */
  enAttente: number;
  /** Libellés des mutations refusées par le serveur, à reprendre. */
  aReprendre: string[];
  /** Éléments (hors refus de numéro) que le serveur de synchronisation ne connaît pas encore. */
  differees: number;
  consentements: EtatConsentements;
}

/** Phrase qui désigne ce que Brigitte devra reprendre : donnée versionnée, valeur par défaut pour un type inconnu. */
export const libelleARepriser = (entityType: string): string =>
  LIBELLES_A_REPRENDRE[typeSync(entityType)] ?? LIBELLE_A_REPRENDRE_PAR_DEFAUT;

/** Nom de l'entité dans le protocole de synchronisation. */
export const typeSync = (entityType: string): string => TYPES_SYNC[entityType] ?? entityType;

/** Transport `sync.push` qui parle le nom de synchronisation des entités : seul point où le nom du contrat est traduit. */
export const transportSync =
  (transport: TransportPush): TransportPush =>
  (corps) =>
    transport({ ...corps, mutations: corps.mutations.map((m) => ({ ...m, entityType: typeSync(m.entityType) })) });

export interface SessionAppel {
  saisir(champ: string, valeur: string | boolean): Promise<ResultatSaisie>;
  terminer(options: { refusNumero: boolean; note?: string }): Promise<BilanFin>;
  /** Au retour du réseau : rejoue la file puis les refus de numéro en réserve. `null` tant que le réseau n'est pas revenu. */
  reprendreReseau(): Promise<BilanRejeu | null>;
  /** Action de Brigitte « Réessayer l'enregistrement » : rejoue les refus de numéro en réserve par la route du contrat. */
  reprendreConsentements(): Promise<EtatAppel>;
  enAttente(): Promise<number>;
  etat(): Promise<EtatAppel>;
  /**
   * Saisies de l'appel en cours de cette personne déjà gardées sur l'appareil (journal, réserve, refus du serveur), pour
   * remplir les champs au retour sur l'écran. Un appel clos (rubrique `issue`) n'est pas repris.
   */
  reprendreSaisies(): Promise<Record<string, string | boolean>>;
}

const TYPE_CONSENTEMENT = 'consentement';

/** Rubrique de l'entité `appel` pour un champ saisi sur M02 : donnée versionnée (`RUBRIQUES_APPEL`). */
function rubriqueDe(champ: string): { regle: RubriqueAppel; question?: string } {
  if (champ.startsWith(PREFIXE_ENTRETIEN)) return { regle: RUBRIQUE_ENTRETIEN_INITIAL, question: champ.slice(PREFIXE_ENTRETIEN.length) };
  return { regle: RUBRIQUES_APPEL[champ] ?? { rubrique: RUBRIQUE_PAR_DEFAUT } };
}

type MutationLocale = Awaited<ReturnType<Journal['enAttente']>>[number];

/** Libellé d'une réponse recueillie (« Libellé : valeur »), tiré des données versionnées de M02 ; jamais un code. */
function ligneRecueillie(champs: Record<string, unknown>): string | null {
  const valeur = champs['valeur'];
  if (typeof valeur !== 'string' || valeur === '') return null;
  if (champs['rubrique'] === RUBRIQUE_ENTRETIEN_INITIAL.rubrique) {
    const q = QUESTIONS_ENTRETIEN_INITIAL.find((x) => x.code === champs['question']);
    if (!q) return null;
    return `${q.question} : ${q.options?.find((o) => o.code === valeur)?.libelle ?? valeur}`;
  }
  const champ = Object.entries(RUBRIQUES_APPEL).find(([, r]) => r.rubrique === champs['rubrique'])?.[0];
  const groupe = [SITUATIONS_MATRIMONIALES, CE_QU_ELLE_DIT].find((g) => g.champ === champ);
  if (groupe) return `${groupe.titre} : ${groupe.choix.find((c) => c.code === valeur)?.libelle ?? valeur}`;
  const texte = CHAMPS_TEXTE_APPEL.find((c) => c.champ === champ);
  return texte ? `${texte.libelle} : ${valeur}` : null;
}

/** Corps du compte rendu d'appel : une ligne par réponse (la dernière saisie l'emporte), le refus de numéro, la note. */
function compteRenduDirect(lignes: readonly MutationLocale[], issue: Record<string, unknown>): { title: string; sourceText: string } {
  const parCle = new Map<string, string>();
  for (const m of lignes) {
    const c = m.op.champs as Record<string, unknown>;
    const ligne = ligneRecueillie(c);
    const cle = `${String(c['rubrique'])}:${String(c['question'] ?? '')}`;
    // Un champ effacé pendant l'appel retire ce qui avait été recueilli (ARB-APPEL-09).
    if (ligne) parCle.set(cle, ligne);
    else if (c['valeur'] === '') parCle.delete(cle);
  }
  const texte = [...parCle.values()];
  if (issue['refusPartageNumero'] === true) texte.push(REFUS_NUMERO);
  if (typeof issue['note'] === 'string' && issue['note'] !== '') texte.push(`${COMPTE_RENDU_APPEL.ligneNote} : ${issue['note']}`);
  const jour = String(issue['termineLe'] ?? '')
    .slice(0, 10)
    .split('-')
    .reverse()
    .join('/');
  return {
    title: COMPTE_RENDU_APPEL.titre.replace('{date}', jour),
    sourceText: texte.length > 0 ? texte.join('\n') : COMPTE_RENDU_APPEL.aucuneInformation,
  };
}

export function creerSessionAppel(services: ServicesAppel, cible: CibleAppel): SessionAppel {
  const { journal } = services;
  const differe = services.differe ?? journal;
  /** Routes directes (production) : tout reste sur l'appareil jusqu'à « Terminer », puis part sans la file de synchronisation. */
  const direct = services.envoyer;
  /** Appels clos que le serveur a refusés (hors coupure réseau) : type de ce qui est à reprendre, par appel. */
  const echecsDirects = new Map<string, string>();
  let envoiDirect: Promise<void> | null = null;
  const estSynchronisee = services.estSynchronisee ?? (() => true);
  /** Regroupe, dans l'entité `appel`, toutes les lignes d'un même appel. */
  const appelId = services.genererId();
  /** Refus de numéro que la route du contrat a refusés (hors coupure réseau) : « à reprendre ». */
  const echecs = new Set<string>();
  let rejeuEnCours: Promise<void> | null = null;
  const attendre = services.attendre ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  /**
   * Écritures sur l'appareil, une à la fois : deux écritures simultanées du journal liraient le même rang et la seconde
   * effacerait la première (touche suivie aussitôt de « Terminer »).
   */
  let ecritures: Promise<unknown> = Promise.resolve();
  const enSerie = <T>(f: () => Promise<T>): Promise<T> => {
    const suite = ecritures.then(f);
    ecritures = suite.catch(() => undefined);
    return suite;
  };

  /**
   * Rejeux de la file d'envoi enchaînés : un rejeu demandé pendant un autre attend la fin du premier puis repart, pour
   * emporter ce qui a été saisi entre-temps. Si un rejeu lancé hors de la session occupe la file (`deja_en_cours`),
   * M02 attend et réessaie tant que le réseau est là (`ATTENTE_REJEU`).
   */
  let rejeux: Promise<unknown> = Promise.resolve();
  const rejouerFile = (): Promise<BilanRejeu> => {
    const suite = rejeux.then(async () => {
      let bilan = await services.rejouer();
      for (let essai = 0; bilan.etat === 'deja_en_cours' && essai < ATTENTE_REJEU.essais && services.enLigne(); essai += 1) {
        await attendre(ATTENTE_REJEU.delaiMs);
        bilan = await services.rejouer();
      }
      return bilan;
    });
    rejeux = suite.catch(() => undefined);
    return suite;
  };
  /** « Terminer » n'agit qu'une fois : un second appui rend le même bilan, sans seconde issue ni seconde clôture. */
  let fin: Promise<BilanFin> | null = null;

  /** Journal qui garde la mutation : la file d'envoi si le serveur connaît le type, sinon la réserve de l'appareil. */
  const journalDe = (entityType: string): Journal => (direct || !estSynchronisee(typeSync(entityType)) ? differe : journal);

  /** Un type que le serveur a fini par déclarer rejoint la file d'envoi, sans ressaisie. Le consentement n'y va jamais. */
  const promouvoirDifferees = async (): Promise<void> => {
    if (differe === journal || direct) return;
    for (const m of await differe.enAttente()) {
      if (m.entityType === TYPE_CONSENTEMENT || !estSynchronisee(typeSync(m.entityType))) continue;
      await journal.enregistrer(m.entityType, m.entityId, m.baseVersion, m.op);
      await differe.retirer([m.mutationId]);
    }
  };

  /** Appels clos gardés sur l'appareil (lignes, compte rendu, tâche), regroupés par appel, dans l'ordre de saisie. */
  const appelsClos = (reserve: readonly MutationLocale[]): Map<string, MutationLocale[]> => {
    const groupes = new Map<string, MutationLocale[]>();
    for (const m of reserve) {
      const id = (m.op.champs as Record<string, unknown>)['appelId'];
      if (m.entityType === TYPE_CONSENTEMENT || typeof id !== 'string') continue;
      groupes.set(id, [...(groupes.get(id) ?? []), m]);
    }
    for (const [id, ms] of groupes) {
      if (!ms.some((m) => m.entityType === 'appel' && (m.op.champs as Record<string, unknown>)['rubrique'] === RUBRIQUE_ISSUE))
        groupes.delete(id);
    }
    return groupes;
  };

  /** Envoie chaque appel clos : compte rendu puis clôture de la tâche ; coupure réseau : on s'arrête, tout reste gardé. */
  const envoyerAppelsClos = async (envoi: EnvoiDirectAppel): Promise<void> => {
    for (const [id, ms] of appelsClos(await differe.enAttente())) {
      const lignes = ms.filter((m) => m.entityType === 'appel');
      const issue = lignes.find((m) => (m.op.champs as Record<string, unknown>)['rubrique'] === RUBRIQUE_ISSUE)!;
      const cr = ms.find((m) => m.entityType === 'compte_rendu');
      const tache = ms.find((m) => m.entityType === 'task');
      const c0 = issue.op.champs as Record<string, unknown>;
      const personId = String(c0['personId']);
      const faite = (etape: string) =>
        ms.some((m) => m.entityType === TYPE_ETAPE && (m.op.champs as Record<string, unknown>)['etape'] === etape);
      const marquer = (etape: string) =>
        differe.enregistrer(TYPE_ETAPE, services.genererId(), 0, {
          kind: 'creer',
          champs: { appelId: id, etape },
          dossierId: String(cr?.op.dossierId ?? ''),
        });
      // Dernière valeur recueillie par rubrique (la dernière saisie l'emporte).
      const valeur = (rubrique: string): string | null => {
        const v = [...lignes].reverse().find((m) => (m.op.champs as Record<string, unknown>)['rubrique'] === rubrique);
        const x = v ? (v.op.champs as Record<string, unknown>)['valeur'] : null;
        return typeof x === 'string' && x.trim() !== '' ? x.trim() : null;
      };
      const profession = valeur(RUBRIQUES_APPEL['profession']!.rubrique);
      const situation = valeur(RUBRIQUES_APPEL['situationMatrimoniale']!.rubrique);
      const adresse = valeur(RUBRIQUES_APPEL['adresse']!.rubrique);
      let etape = 'personne';
      try {
        if (envoi.personne && (profession || situation) && !faite('fiche')) {
          await envoi.personne(personId, Number(c0['versionPersonne'] ?? 0), {
            ...(profession ? { profession } : {}),
            ...(situation ? { maritalStatus: situation } : {}),
          });
          await marquer('fiche');
        }
        etape = 'coordonnee';
        if (envoi.coordonnee && adresse && !faite('adresse')) {
          await envoi.coordonnee(personId, { type: 'address', value: adresse });
          await marquer('adresse');
        }
        etape = 'compte_rendu';
        if (cr) {
          const c = issue.op.champs as Record<string, unknown>;
          await envoi.compteRendu(String(cr.op.dossierId), {
            kind: COMPTE_RENDU_APPEL.kind,
            personId: String(c['personId']),
            ...compteRenduDirect(lignes, c),
          });
          const etapes = (await differe.enAttente()).filter(
            (m) => m.entityType === TYPE_ETAPE && (m.op.champs as Record<string, unknown>)['appelId'] === id,
          );
          await differe.retirer([...lignes, cr, ...etapes].map((m) => m.mutationId));
        }
        etape = 'tache';
        if (tache) {
          await envoi.tache(tache.entityId, tache.baseVersion, { status: 'fait' });
          await differe.retirer([tache.mutationId]);
        }
        echecsDirects.delete(id);
      } catch (e) {
        if (e instanceof ErreurApi && e.code === 'RESEAU') return;
        echecsDirects.set(id, etape);
      }
    }
  };

  /** Un seul envoi direct à la fois : un second appel attend le premier (jamais deux comptes rendus pour un appel). */
  const envoyerDirect = async (): Promise<void> => {
    if (!direct || !services.enLigne()) return;
    while (envoiDirect) await envoiDirect;
    envoiDirect = enSerie(() => envoyerAppelsClos(direct)).finally(() => {
      envoiDirect = null;
    });
    await envoiDirect;
  };

  const etat = async (): Promise<EtatAppel> => {
    const reserve = differe === journal ? [] : await differe.enAttente();
    const refus = reserve.filter((m) => m.entityType === TYPE_CONSENTEMENT);
    if (direct) {
      return {
        // Ce qui attend le serveur : les appels terminés pas encore transmis (un appel = une saisie à transmettre).
        enAttente: appelsClos(reserve).size,
        aReprendre: [...echecsDirects.values()].map(libelleARepriser),
        differees: 0,
        consentements: { enAttente: refus.length, aReprendre: refus.some((m) => echecs.has(m.mutationId)) },
      };
    }
    return {
      enAttente: await journal.compter(),
      aReprendre: (await journal.conflits()).map((c) => libelleARepriser(c.mutation.entityType)),
      differees: reserve.length - refus.length,
      consentements: { enAttente: refus.length, aReprendre: refus.some((m) => echecs.has(m.mutationId)) },
    };
  };

  const rejeuConsentements = async (): Promise<void> => {
    const envoyer = services.enregistrerConsentement;
    if (!envoyer) return;
    for (const m of await differe.enAttente()) {
      if (m.entityType !== TYPE_CONSENTEMENT) continue;
      const { personId, purpose, granted, channel } = m.op.champs as Record<string, unknown>;
      try {
        await envoyer(String(personId), { purpose, granted, channel } as ConsentementAEnregistrer);
        await differe.retirer([m.mutationId]);
        echecs.delete(m.mutationId);
      } catch (e) {
        if (e instanceof ErreurApi && e.code === 'RESEAU') {
          echecs.delete(m.mutationId);
          return;
        }
        echecs.add(m.mutationId);
      }
    }
  };

  /** Rejoue chaque refus de numéro en réserve par la route du contrat ; un seul rejeu à la fois (pas de double envoi). */
  const rejouerConsentements = async (): Promise<void> => {
    if (!services.enregistrerConsentement || differe === journal || !services.enLigne()) return;
    if (rejeuEnCours) return rejeuEnCours;
    const rejeu = rejeuConsentements();
    rejeuEnCours = rejeu;
    try {
      await rejeu;
    } finally {
      rejeuEnCours = null;
    }
  };

  const envoyerSiPossible = async (): Promise<ResultatSaisie> => {
    await enSerie(promouvoirDifferees);
    if (direct) {
      await envoyerDirect();
      await rejouerConsentements();
    } else if (services.enLigne()) {
      await rejouerFile();
      await rejouerConsentements();
    }
    const { enAttente, aReprendre } = await etat();
    // Un refus sort de la file d'envoi : sans ce contrôle, la file vide ferait croire que tout est enregistré.
    const resultat = aReprendre.length > 0 ? 'a_reprendre' : enAttente === 0 ? 'envoyee' : 'gardee';
    return { etat: resultat, enAttente, ...(aReprendre.length > 0 ? { aReprendre: aReprendre.length } : {}) };
  };

  /** Ajoute une ligne à l'entité `appel` (ajout seul : jamais de conflit, écriture équipe). */
  const consigner = (champs: Record<string, unknown>): Promise<unknown> =>
    journalDe('appel').enregistrer('appel', services.genererId(), 0, { kind: 'creer', champs, dossierId: cible.dossierId });

  return {
    async saisir(champ, valeur) {
      const { regle, question } = rubriqueDe(champ);
      await enSerie(() =>
        consigner({
          appelId,
          rubrique: regle.rubrique,
          valeur,
          agentId: services.agentId,
          saisieLe: services.horloge(),
          // L'entretien initial appartient au dossier ; le reste décrit la personne appelée.
          ...(regle.dossier ? { question } : { personId: cible.personId }),
          ...(regle.coordonnee ?? {}),
        }),
      );
      // Routes directes : la saisie reste sur l'appareil jusqu'à « Terminer » (aucun envoi à la frappe).
      if (direct) return { etat: 'gardee', enAttente: (await etat()).enAttente };
      return envoyerSiPossible();
    },

    terminer(options) {
      // Un échec (panne du journal local) rouvre « Terminer » : Brigitte peut réessayer.
      fin ??= terminerUneFois(options).catch((e: unknown) => {
        fin = null;
        throw e;
      });
      return fin;
    },

    async reprendreReseau() {
      if (!services.enLigne()) return null;
      if (direct) {
        const avant = (await etat()).enAttente;
        await envoyerDirect();
        await rejouerConsentements();
        const restantes = (await etat()).enAttente;
        return { etat: 'termine', envoyees: avant - restantes, conflits: echecsDirects.size, restantes };
      }
      await enSerie(promouvoirDifferees);
      const bilan = await rejouerFile();
      await rejouerConsentements();
      return bilan;
    },

    async reprendreConsentements() {
      await rejouerConsentements();
      return etat();
    },

    enAttente: () => journal.compter(),
    etat,

    async reprendreSaisies() {
      const refuses = (await journal.conflits()).map((c) => c.mutation);
      const toutes = [...(await journal.enAttente()), ...(differe === journal ? [] : await differe.enAttente()), ...refuses]
        .filter((m) => m.entityType === 'appel')
        .sort((a, b) => a.creeeA.localeCompare(b.creeeA));
      const reprises: Record<string, string | boolean> = {};
      for (const m of toutes) {
        const c = (m.op.champs ?? {}) as Record<string, unknown>;
        const entretien = c['rubrique'] === RUBRIQUE_ENTRETIEN_INITIAL.rubrique;
        // L'entretien initial appartient au dossier ; tout le reste à la personne appelée.
        if (entretien ? m.op.dossierId !== cible.dossierId : c['personId'] !== cible.personId) continue;
        if (c['rubrique'] === RUBRIQUE_ISSUE) {
          for (const k of Object.keys(reprises)) delete reprises[k];
          continue;
        }
        const valeur = c['valeur'];
        if (typeof valeur !== 'string' && typeof valeur !== 'boolean') continue;
        if (entretien && typeof c['question'] === 'string') {
          reprises[`${PREFIXE_ENTRETIEN}${c['question']}`] = valeur;
          continue;
        }
        const champ = Object.entries(RUBRIQUES_APPEL).find(([, r]) => r.rubrique === c['rubrique'])?.[0];
        if (champ) reprises[champ] = valeur;
      }
      return reprises;
    },
  };

  async function terminerUneFois({ refusNumero, note }: { refusNumero: boolean; note?: string }): Promise<BilanFin> {
    await enSerie(async () => {
      if (refusNumero) {
        // ConsentRecord du contrat, gardé en réserve (jamais dans sync.push) puis enregistré par la route dédiée.
        await differe.enregistrer(TYPE_CONSENTEMENT, services.genererId(), 0, {
          kind: 'creer',
          champs: { personId: cible.personId, ...CONSENTEMENT_REFUS_NUMERO },
          dossierId: cible.dossierId,
        });
      }
      await consigner({
        appelId,
        rubrique: RUBRIQUE_ISSUE,
        personId: cible.personId,
        agentId: services.agentId,
        outcome: 'joint',
        refusPartageNumero: refusNumero,
        termineLe: services.horloge(),
        ...(note ? { note } : {}),
        // Version de la fiche lue à l'ouverture de l'appel : If-Match de l'écriture sur la fiche (routes directes).
        ...(direct ? { versionPersonne: cible.versionPersonne } : {}),
      });
      await journalDe('compte_rendu').enregistrer('compte_rendu', services.genererId(), 0, {
        kind: 'creer',
        champs: {
          modele: MODELE_COMPTE_RENDU,
          statut: 'a_relire',
          source: 'appel',
          personId: cible.personId,
          ...(note ? { note } : {}),
          ...(direct ? { appelId } : {}),
        },
        dossierId: cible.dossierId,
      });
      if (cible.tacheId) {
        await journalDe('task').enregistrer('task', cible.tacheId, cible.versionTache ?? 0, {
          kind: 'modifier',
          champs: { status: 'fait', ...(direct ? { appelId } : {}) },
          dossierId: cible.dossierId,
        });
      }
    });
    await envoyerSiPossible();
    const apres = await etat();
    const refusARepriser = apres.consentements.aReprendre ? [libelleARepriser(TYPE_CONSENTEMENT)] : [];
    return {
      consentementEnregistre: refusNumero && apres.consentements.enAttente === 0,
      compteRendu: 'a_relire',
      aReprendre: apres.aReprendre.length + refusARepriser.length,
      elementsARepriser: [...apres.aReprendre, ...refusARepriser],
      differees: apres.differees,
    };
  }
}

const saisies = (n: number): string => `${n} ${n > 1 ? 'saisies' : 'saisie'}`;

export function libelleBandeauAppel(enLigne: boolean, enAttente: number): string | null {
  if (enLigne) return enAttente > 0 ? `Réseau rétabli. ${saisies(enAttente)} ${enAttente > 1 ? 'partent' : 'part'} en ce moment.` : null;
  const base = 'Réseau faible. Vos saisies sont gardées sur le téléphone et partiront au retour du réseau.';
  return enAttente > 0 ? `${base} ${saisies(enAttente)} en attente.` : base;
}

const pluriel = (n: number, un: string, plusieurs: string): string => (n > 1 ? plusieurs : un);

/** Message affiché quand le serveur a refusé des saisies : ce qui est à reprendre, et quoi faire. */
export function messageARepriser(elements: readonly string[]): string {
  const n = elements.length;
  return `Le serveur n’a pas enregistré ${pluriel(n, 'cette saisie', 'ces saisies')} : ${pluriel(n, 'elle est', 'elles sont')} à reprendre (${[...new Set(elements)].join(' ; ')}). Reprenez-${pluriel(n, 'la', 'les')} ou prévenez le Cabinet MAAT.`;
}

/** Message sur le refus de partage du numéro : gardé en attente du réseau, ou à reprendre quand le serveur l'a refusé. */
export function messageConsentement(etat: EtatConsentements): string | null {
  if (etat.enAttente === 0) return null;
  if (etat.aReprendre)
    return 'Le refus de partage du numéro n’a pas pu être enregistré : il est à reprendre. Utilisez « Réessayer l’enregistrement » ou prévenez le Cabinet MAAT.';
  return 'Le refus de partage du numéro est gardé sur cet appareil. Il sera enregistré au retour du réseau, sans que vous ayez à le ressaisir. Cabinet MAAT';
}

/** Message affiché quand des éléments de l'appel restent sur l'appareil, le serveur n'en connaissant pas encore le type. */
export function messageDifferees(n: number): string {
  return `${n} ${pluriel(n, 'élément de cet appel est gardé', 'éléments de cet appel sont gardés')} sur cet appareil : le serveur ne ${pluriel(n, 'l’accepte', 'les accepte')} pas encore. ${pluriel(n, 'Il est', 'Ils sont')} à reprendre dès qu’il ${pluriel(n, 'sera', 'seront')} accepté${pluriel(n, '', 's')}. Cabinet MAAT`;
}
